import React, { useState, useRef, useEffect } from "react";

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SelectedItem {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  allOptionLabel?: string;
  emptyValue?: string; // The value to use for "all" or "none" option (default: "all")
  hideAllOption?: boolean; // Hide the "All" option from the dropdown
  className?: string;
  inputClassName?: string; // Override input background/styling
  disabled?: boolean;
  label?: string; // Optional floating label
  selectedItems?: SelectedItem[]; // For multi-select: chips shown inside the input
  onRemoveItem?: (value: string) => void; // Callback to remove a selected item
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Search...",
  allOptionLabel = "All",
  emptyValue = "all",
  hideAllOption = false,
  className = "",
  inputClassName,
  disabled = false,
  label,
  selectedItems,
  onRemoveItem,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get the display label for current value
  const selectedOption = options.find((opt) => opt.value === value);
  const displayValue =
    value === emptyValue ? allOptionLabel : selectedOption?.label || "";

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleInputFocus = () => {
    if (disabled) return;
    setIsOpen(true);
    setSearchTerm("");
  };

  const hasSelectedItems = selectedItems && selectedItems.length > 0;

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Container with optional chips */}
      <div
        className={`relative border border-base-300 rounded-md ${inputClassName || "bg-base-100"} ${
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        }`}
        onClick={() => {
          if (!disabled) inputRef.current?.focus();
        }}
      >
        {/* Floating label */}
        {label && (
          <label className="absolute left-4 top-1 text-xs text-base-content/60 font-grotesk pointer-events-none z-10">
            {label}
          </label>
        )}

        {/* Selected items chips */}
        {hasSelectedItems && (
          <div
            className={`flex flex-wrap gap-1.5 px-4 ${label ? "pt-5 pb-1" : "pt-3 pb-1"}`}
          >
            {selectedItems.map((item) => (
              <span
                key={item.value}
                className="inline-flex items-center p-2 rounded-md text-sm font-geist bg-neutral text-white font-medium"
              >
                {item.label}
                {onRemoveItem && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveItem(item.value);
                    }}
                    className="ml-1.5 hover:text-white/60"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </span>
            ))}
          </div>
        )}

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : hasSelectedItems ? "" : displayValue}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={handleInputFocus}
          placeholder={
            isOpen
              ? placeholder
              : hasSelectedItems
                ? "Add more..."
                : displayValue || placeholder
          }
          disabled={disabled}
          className={`appearance-none block w-full pl-4 pr-12 border-0 font-medium leading-5 bg-transparent text-base-content/80 focus:outline-none focus:ring-0 font-grotesk text-base sm:text-sm cursor-pointer ${
            disabled ? "cursor-not-allowed" : ""
          } ${hasSelectedItems ? "py-1.5" : label ? "pt-6 pb-2 py-4" : "py-4"}`}
        />

        {/* Chevron icon */}
        <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none">
          <svg
            className={`w-5 h-5 text-base-content/50 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {/* Empty/All option */}
          {!hideAllOption && (
            <button
              type="button"
              onClick={() => handleSelect(emptyValue)}
              className={`w-full text-left px-4 py-3 hover:bg-base-200 focus:bg-base-200 focus:outline-none text-base sm:text-sm font-grotesk ${
                value === emptyValue ? "bg-base-200 font-medium" : ""
              }`}
            >
              {allOptionLabel}
            </button>
          )}

          {/* Filtered options */}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={`w-full text-left px-4 py-3 hover:bg-base-200 focus:bg-base-200 focus:outline-none text-base sm:text-sm font-grotesk ${
                  value === option.value ? "bg-base-200 font-medium" : ""
                }`}
              >
                {option.label}
              </button>
            ))
          ) : (
            <div className="px-4 py-3 text-base sm:text-sm text-base-content/50 font-grotesk">
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
